import { Link } from "react-router";

import { Price } from "@/components/ui/price";
import { PUBLIC_ROUTES } from "@/constants/routes";
import type { ConversationProductSummary } from "@/features/messaging/types/chat";

interface PinnedProductCardProps {
  product: ConversationProductSummary;
}

/** docs/ui/component-inventory.md's `PinnedProductCard`: keeps the listing
 * a conversation is about visible at the top of the thread. `status ===
 * "archived"` doubles as this component's "listing no longer available"
 * fallback state — a deleted product can't be fetched at all (the chat
 * would have no product to show), but an archived one still has a real
 * summary to render, just visually muted. */
export function PinnedProductCard({ product }: PinnedProductCardProps) {
  const isUnavailable = product.status !== "active";

  return (
    <Link
      to={PUBLIC_ROUTES.productDetails.replace(":slug", product.slug)}
      className="flex items-center gap-3 border-b border-border bg-card px-4 py-3 hover:bg-muted"
    >
      <div className="h-12 w-12 shrink-0 overflow-hidden rounded-md bg-muted">
        {product.image && <img src={product.image} alt="" className="h-full w-full object-cover" />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-body-sm font-medium text-foreground">{product.title}</p>
        {isUnavailable ? (
          <p className="text-caption text-muted-foreground">No longer available</p>
        ) : (
          <Price value={product.price} size="body" />
        )}
      </div>
    </Link>
  );
}
